import { useState } from 'react';
import { Button, Group, Paper, Stack, Text } from '@cowbell/ui';
import { useStartConnectionAuthorize } from '../../hooks/useUserConnections';
import type { ConnectIntegrationAction } from '../../types/action-cards.types';
import { ProviderIcon } from '../ProviderIcon';
import { ConnectIntegrationModal } from './ConnectIntegrationModal';
import styles from './ConnectIntegrationPromptShim.module.css';

/** Capitalise the agent's reason so it reads as a sentence in the card. */
function asSentence(reason: string): string {
  const trimmed = reason.trim();
  if (!trimmed) return '';
  const withCapital = trimmed[0].toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(withCapital) ? withCapital : `${withCapital}.`;
}

export interface ConnectIntegrationPromptShimProps {
  readonly action: ConnectIntegrationAction;
}

/**
 * Inline prompt shown when an agent needs a per-user OAuth connection the
 * caller doesn't have (OMNI-814).
 *
 * Two steps on purpose: `Continue` opens a consent modal naming the account
 * and the access being granted, and only its `Connect` starts the OAuth
 * redirect. Sending someone straight off to a vendor consent screen from a
 * chat bubble gives them nothing to weigh first.
 */
export function ConnectIntegrationPromptShim({
  action,
}: ConnectIntegrationPromptShimProps) {
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const {
    mutate: startAuthorize,
    isLoading,
    cancel: cancelAuthorize,
  } = useStartConnectionAuthorize();

  /**
   * Closing the modal must also abandon an authorize already in flight.
   * The redirect fires from the mutation's `onSuccess`, so without this a
   * Connect-then-Cancel would still throw the user at the vendor's consent
   * screen after they had backed out.
   */
  const handleCancel = () => {
    setConfirming(false);
    cancelAuthorize();
  };

  if (dismissed) {
    return null;
  }

  return (
    <>
      <Paper withBorder p="md" className={styles.card}>
        <Stack gap="sm">
          <Group gap="sm" align="flex-start" wrap="nowrap">
            <ProviderIcon
              provider={action.provider}
              displayName={action.displayName}
              size={32}
            />
            <Stack gap={2} className={styles.copy}>
              <Text fw={600}>{action.displayName}</Text>
              {action.reason && (
                <Text size="sm" c="dimmed">
                  {asSentence(action.reason)}
                </Text>
              )}
            </Stack>
          </Group>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setDismissed(true)}
            >
              No thanks
            </Button>
            <Button size="sm" onClick={() => setConfirming(true)}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Paper>

      <ConnectIntegrationModal
        opened={confirming}
        provider={action.provider}
        displayName={action.displayName}
        isConnecting={isLoading}
        onCancel={handleCancel}
        onConfirm={() => startAuthorize(action.provider)}
      />
    </>
  );
}
